import { createSelector } from 'reselect';
import { isGamePageSelector } from '@Utils/facade/edgeTs';
import { RootState } from '@Utils/core/redux';

const LANGUAGE_MODAL_STATE = 'state/languageModalState/change';
const LOGOUT_MODAL_STATE = 'state/logoutModalState/change';
const GUEST_MODAL_STATE = 'state/guestModalState/change';
const GUEST_MODAL_RESULT = 'state/guestModalResult/change';
const DROP_STATE_CHANGE = 'state/dropState/change';
const CHAT_OPEN_STATE_CHANGE = 'state/chatOpenState/change';
const CHAT_CONVERSATION_ID_CHANGE = 'state/chatConversationId/change';
const CHAT_USER_ID_CHANGE = 'state/chatUserId/change';
const SET_GAME_SERVER_ID = 'state/gameServerId/set';
const CLIENT_UPDATE_STATE_CHANGE = 'state/clientUpdate/change';

export const DROP_STATE = {
  CHAT_OPEN: 'dropChatIsOpen',
  CLOSED: 'dropClosed',
} as const;

export const GUEST_MODAL_STATES = {
  DISABLED: false,
  LEAVE_PAGE: 'LEAVE_PAGE',
} as const;

export const CLIENT_UPDATE_STATES = {
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
} as const;

interface GlobalStateModule {
  languageModalState: boolean;
  logoutModalState: boolean;
  chatOpenState: boolean;
  chatConversationId: null;
  chatUserId: null;
  dropState: (typeof DROP_STATE)[keyof typeof DROP_STATE];
  gameServerId: string;
  gameId: null | string;
  clientUpdateState: (typeof CLIENT_UPDATE_STATES)[keyof typeof CLIENT_UPDATE_STATES];
  guestModalResult: null | boolean;
  guestModalState: (typeof GUEST_MODAL_STATES)[keyof typeof GUEST_MODAL_STATES];
  themeId?: string;
}

const defaultState: GlobalStateModule = {
  languageModalState: false,
  logoutModalState: false,
  chatOpenState: false,
  chatConversationId: null,
  chatUserId: null,
  dropState: DROP_STATE.CLOSED,
  gameServerId: '',
  gameId: null,
  /**
   * Promo banner on game load
   */
  clientUpdateState: CLIENT_UPDATE_STATES.CLOSED,
  guestModalResult: null,
  guestModalState: false,
};

export default (state = defaultState, action: any) => {
  switch (action.type) {
    case LANGUAGE_MODAL_STATE: {
      return {
        ...state,
        languageModalState: action.payload,
      };
    }
    case LOGOUT_MODAL_STATE: {
      return {
        ...state,
        logoutModalState: action.payload,
      };
    }
    case GUEST_MODAL_STATE: {
      return {
        ...state,
        guestModalState: action.payload,
      };
    }
    case GUEST_MODAL_RESULT: {
      return {
        ...state,
        guestModalResult: action.payload,
      };
    }
    case DROP_STATE_CHANGE: {
      return {
        ...state,
        dropState: action.payload,
      };
    }
    case CHAT_OPEN_STATE_CHANGE: {
      return {
        ...state,
        chatOpenState: action.payload,
      };
    }
    case CHAT_CONVERSATION_ID_CHANGE: {
      return {
        ...state,
        chatConversationId: action.payload,
      };
    }
    case CHAT_USER_ID_CHANGE: {
      return {
        ...state,
        chatUserId: action.payload,
      };
    }
    case SET_GAME_SERVER_ID: {
      return {
        ...state,
        gameServerId: action.payload.gameServerId,
        gameId: action.payload.gameId,
        themeId: action.payload.themeId,
      };
    }
    case CLIENT_UPDATE_STATE_CHANGE: {
      return {
        ...state,
        clientUpdateState: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

const withLocalState = (state: RootState): GlobalStateModule => state.state;

export const chatConversationIdSelector = createSelector(
  withLocalState,
  (state) => state.chatConversationId,
);
export const chatUserIdSelector = createSelector(withLocalState, (state) => state.chatUserId);
export const chatOpenStateSelector = createSelector(withLocalState, (state) => state.chatOpenState);
export const dropStateSelector = createSelector(withLocalState, (state) => state.dropState);
export const languageModalStateSelector = createSelector(
  withLocalState,
  (state) => state.languageModalState,
);
export const logoutModalStateSelector = createSelector(
  withLocalState,
  (state) => state.logoutModalState,
);
export const guestModalStateSelector = createSelector(
  withLocalState,
  (state) => state.guestModalState,
);
export const guestModalResultSelector = createSelector(
  withLocalState,
  (state) => state.guestModalResult,
);
export const gameIdSelector = createSelector(withLocalState, (state) => state.gameId);
export const gameServerIdSelector = createSelector(withLocalState, (state) => state.gameServerId);
export const gameThemeIdSelector = createSelector(withLocalState, (state) => state.themeId);
export const clientUpdateStateSelector = createSelector(
  withLocalState,
  (state) => state.clientUpdateState,
);

export const setClientUpdateState = (payload: GlobalStateModule['clientUpdateState']) => ({
  type: CLIENT_UPDATE_STATE_CHANGE,
  payload,
});

export const setLanguageModalState = (state: GlobalStateModule['languageModalState']) => ({
  type: LANGUAGE_MODAL_STATE,
  payload: state,
});

export const setLogoutModalState = (state: GlobalStateModule['logoutModalState']) => ({
  type: LOGOUT_MODAL_STATE,
  payload: state,
});

export const setGuestModalState = (state: GlobalStateModule['guestModalState']) => ({
  type: GUEST_MODAL_STATE,
  payload: state,
});

export const setGuestModalResult = (state: GlobalStateModule['guestModalResult']) => ({
  type: GUEST_MODAL_RESULT,
  payload: state,
});

const chatOpenStateChange = (newState: GlobalStateModule['chatOpenState']) => ({
  type: CHAT_OPEN_STATE_CHANGE,
  payload: newState,
});

export const setDropState =
  (newState: (typeof DROP_STATE)[keyof typeof DROP_STATE]) =>
  (dispatch: any, getState: () => RootState) => {
    const chatOpenState = chatOpenStateSelector(getState());
    const chatState = newState === DROP_STATE.CHAT_OPEN;
    if (chatState !== chatOpenState) {
      dispatch(chatOpenStateChange(chatState));
      if (!chatState) {
        dispatch({
          type: CHAT_CONVERSATION_ID_CHANGE,
          payload: null,
        });
        dispatch({
          type: CHAT_USER_ID_CHANGE,
          payload: null,
        });
      }
    }

    return dispatch({
      type: DROP_STATE_CHANGE,
      payload: newState,
    });
  };

export const openChatWithUserId =
  ({ conversationId = 0, userId = 0 }) =>
  (dispatch: any) => {
    dispatch({
      type: CHAT_CONVERSATION_ID_CHANGE,
      payload: conversationId,
    });
    dispatch({
      type: CHAT_USER_ID_CHANGE,
      payload: userId,
    });
    dispatch(setDropState(DROP_STATE.CHAT_OPEN));
  };

export const setGameServerId = ({
  gameServerId,
  gameId,
  themeId,
}: {
  gameServerId: GlobalStateModule['gameServerId'];
  gameId: GlobalStateModule['gameId'];
  themeId: GlobalStateModule['themeId'];
}) => ({
  type: SET_GAME_SERVER_ID,
  payload: {
    gameServerId,
    gameId,
    themeId,
  },
});

const guestCheckModal =
  (modalId: (typeof GUEST_MODAL_STATES)[keyof typeof GUEST_MODAL_STATES]) =>
  (dispatch: any, getState: () => RootState) =>
    new Promise<void>((resolve, reject) => {
      const state = getState();
      const isGamePage = isGamePageSelector(state);

      if (isGamePage && modalId === GUEST_MODAL_STATES.LEAVE_PAGE) {
        const waitForModalClose = () => {
          setTimeout(() => {
            const newState = getState();
            const guestModalState = guestModalStateSelector(newState);
            const guestModalResult = guestModalResultSelector(newState);

            if (guestModalState) {
              waitForModalClose();
            } else if (guestModalResult) {
              resolve();
            } else {
              reject();
            }
          }, 100);
        };
        if (!Object.prototype.hasOwnProperty.call(GUEST_MODAL_STATES, modalId)) {
          console.error(`Unknown guest modal id! ${modalId}`);
          resolve();
        }

        dispatch(setGuestModalState(modalId));
        waitForModalClose();
      } else {
        resolve();
      }
    });

export const guestCheckLeaveModal = () => guestCheckModal(GUEST_MODAL_STATES.LEAVE_PAGE);
